import { FC, useState, useCallback, useMemo, useReducer, useEffect, useRef, CSSProperties } from "react";
import TestDropdown from "./TestDropdown";
import TestForm from "./TestForm";
import CustomSelect from "../../../../../../../CommonComponents/CustomSelect";
import { CriticalIcon } from "../../../../../../../Missions/Satellites/TestCase/svgs/TestCaseSvgs";
import { deriveIsCritical } from "../../../../../../../Missions/Satellites/TestCase/TestCaseHelpers";
import type { Option, TestCaseConfig, TestPlanConfig } from "../../types/consoleTypes";
import useUrlParams from "../../../../../../hooks/useUrlParams";
import { showToastMessage } from "../../../../../../../../utils/commonFunctions";
import { SESSION_KEY_FAILURE_HANDLING, CUSTOM_EVENT_STORAGE_UPDATE } from "../../../../../../../../utils/constants";
import ValidateService from "../../../../../../../../service/ValidateService";

// ─── Shared utilities ──────────────────────────────────────────────────────────

function flattenObject(value: any, prefix = "", out: Record<string, string> = {}): Record<string, string> {
  if (value == null) return out;
  if (Array.isArray(value)) {
    value.forEach((v, i) => flattenObject(v, prefix ? `${prefix}[${i}]` : `[${i}]`, out));
    return out;
  }
  if (typeof value === "object") {
    Object.entries(value).forEach(([k, v]) => flattenObject(v, prefix ? `${prefix}.${k}` : k, out));
    return out;
  }
  out[prefix] = String(value);
  return out;
}

function toTestCaseConfig(src: any): TestCaseConfig | null {
  if (!src?.testCaseId || !Array.isArray(src?.testCaseDetails)) return null;
  return {
    testCaseId: src.testCaseId,
    testPlanId: src.testPlanId,
    version: src.version,
    createdAt: src.createdAt,
    updatedAt: src.updatedAt,
    testCaseDetails: src.testCaseDetails.map((entry: any, idx: number) => ({
      operationId: String(entry?.operationId ?? "") || `entry-${idx + 1}`,
      testCaseEntryType: (entry?.testCaseEntryType ?? "TC") as "TC" | "TASK",
      testCaseEntryStatus: entry?.testCaseEntryStatus ?? "",
      testCaseEntryDetails: flattenObject(entry?.testCaseEntryDetails ?? {}),
      isCritical: !!(entry?.isCritical ?? entry?.CommandDetails?.isCritical),
    })),
  };
}

function toTestCaseShellOption(testCase: any, plan?: TestPlanConfig, fallbackLabel?: string): Option<TestCaseConfig> | null {
  const testCaseId = testCase?.testCaseId;
  if (!testCaseId) return null;
  const isCritical = deriveIsCritical(testCase);
  return {
    label: testCase?.testCaseName ?? testCase?.name ?? fallbackLabel ?? testCase?.testCaseId ?? "Unnamed test case",
    value: {
      testCaseId,
      testPlanId: testCase?.testPlanId ?? plan?.testPlanId,
      version: testCase?.version,
      createdAt: testCase?.createdAt ?? plan?.createdAt,
      updatedAt: testCase?.updatedAt ?? plan?.updatedAt,
      isCritical,
    } as TestCaseConfig,
    icon: isCritical ? <CriticalIcon /> : undefined,
  };
}

function toTestCaseOptionFromPlan(testCase: any, plan: TestPlanConfig): Option<TestCaseConfig> | null {
  const shell = toTestCaseShellOption(testCase, plan);
  const config = toTestCaseConfig({
    ...testCase,
    testPlanId: plan.testPlanId,
    createdAt: plan.createdAt,
    updatedAt: plan.updatedAt,
  });
  if (!shell || !config) return null;
  const isCritical = deriveIsCritical(config);
  return {
    ...shell,
    value: config,
    icon: isCritical ? <CriticalIcon /> : undefined,
  };
}

function getResponseData(res: any) {
  return res?.data?.data ?? res?.data;
}

function extractTestPlanDetail(response: any): TestPlanConfig | null {
  const payload = response?.data?.data ?? response?.data;
  if (!payload || typeof payload !== "object") return null;
  return payload as TestPlanConfig;
}

function clearStoredTestSelections(): void {
  [
    "testTestCase",
    "testCaseId",
    "testFormValues",
    "testPlanPlan",
    "testPlanCaseId",
    "testPlanFormValues",
    "testPlanId",
  ].forEach((key) => sessionStorage.removeItem(key));
  window.dispatchEvent(new Event("storageUpdate"));
}

// ─── Test plan form reducer ────────────────────────────────────────────────────

type TestPlanState = {
  valuesByCase: Record<string, Record<string, string>>;
  validityByCase: Record<string, boolean>;
};

type TestPlanAction =
  | { type: "UPDATE_CASE"; caseKey: string; values: Record<string, string>; isValid: boolean }
  | { type: "RESET" };

const initialTestPlanState: TestPlanState = { valuesByCase: {}, validityByCase: {} };

function testPlanReducer(state: TestPlanState, action: TestPlanAction): TestPlanState {
  switch (action.type) {
    case "UPDATE_CASE":
      return {
        valuesByCase: { ...state.valuesByCase, [action.caseKey]: action.values },
        validityByCase: { ...state.validityByCase, [action.caseKey]: action.isValid },
      };
    case "RESET":
      return initialTestPlanState;
  }
}

const STORAGE_KEYS = {
  "TEST":      { selection: "testTestCase",  caseId: "testCaseId",      formValues: "testFormValues" },
  "TEST PLAN": { selection: "testPlanPlan",  caseId: "testPlanCaseId",  formValues: "testPlanFormValues" },
} as const;

const TestCaseDetailsShimmer = () => (
  <div className="test__form__shimmer" aria-label="Loading test case details">
    {Array.from({ length: 4 }).map((_, i) => (
      <div className="test__entry__card test__entry__card--shimmer" key={i}>
        <div className="test__entry__card__header">
          <span className="test__shimmer test__shimmer--caret" />
          <span className="test__shimmer test__shimmer--title" style={{ '--test-shimmer-w': `${46 + (i % 3) * 12}%` } as CSSProperties} />
        </div>
        {i === 0 && (
          <div className="test__entry__card__body">
            <div className="test__entry__field__row">
              <div className="test__entry__field__col">
                <span className="test__shimmer test__shimmer--label" />
                <span className="test__shimmer test__shimmer--input" />
              </div>
              <div className="test__entry__field__col">
                <span className="test__shimmer test__shimmer--label" />
                <span className="test__shimmer test__shimmer--input" />
              </div>
            </div>
          </div>
        )}
      </div>
    ))}
  </div>
);

// ─── Component ─────────────────────────────────────────────────────────────────

interface TestFormSectionProps {
  mode: "TEST" | "TEST PLAN";
  formContainerHeight: number;
  onFormChange: (values: Record<string, string>, isValid: boolean) => void;
}

const TestPlanFormShimmer: FC = () => (
  <div
    className="test__plan__loading"
    role="status"
    aria-live="polite"
    aria-label="Loading test plan details"
  >
    {[0, 1].map((item) => (
      <div className="test__plan__loading__context" key={item}>
        <div className="test__plan__shimmer test__plan__loading__label" />
        <div className="test__plan__loading__card">
          <div className="test__plan__loading__header">
            <div className="test__plan__shimmer test__plan__loading__caret" />
            <div className="test__plan__shimmer test__plan__loading__title" />
          </div>
          <div className="test__plan__loading__fields">
            {[0, 1, 2, 3].map((field) => (
              <div className="test__plan__loading__field" key={field}>
                <div className="test__plan__shimmer test__plan__loading__field-label" />
                <div className="test__plan__shimmer test__plan__loading__input" />
              </div>
            ))}
          </div>
        </div>
      </div>
    ))}
    <span className="test__plan__loading__sr-only">Loading test plan details</span>
  </div>
);

const TestFormSection: FC<TestFormSectionProps> = ({ mode, formContainerHeight, onFormChange }) => {
  const { mission_id, sat_id } = useUrlParams();
  const keys = STORAGE_KEYS[mode];
  const isTestPlan = mode === "TEST PLAN";

  const [selectedTestCase, setSelectedTestCase] = useState<Option<TestCaseConfig> | null>(null);
  // ─── Console Failure Handling State ───
  // We use sessionStorage to hold the failure handling mode ('ABORT_ON_FAILURE' or 'SKIP_AND_CONTINUE')
  // so that sibling execution components (e.g., FormSubmit.tsx) can easily read the state at run time.
  const [failureHandlingOption, setFailureHandlingOption] = useState<Option<string>>(() => {
    const saved = sessionStorage.getItem(SESSION_KEY_FAILURE_HANDLING);
    if (saved === 'SKIP_AND_CONTINUE') {
      return { label: "Skip & Continue", value: "SKIP_AND_CONTINUE" };
    }
    return { label: "Abort on Failure", value: "ABORT_ON_FAILURE" };
  });

  const handleFailureHandlingChange = (opt: Option<string> | null) => {
    const val = opt?.value ?? "ABORT_ON_FAILURE";
    const label = opt?.label ?? "Abort on Failure";
    setFailureHandlingOption({ label, value: val });
    sessionStorage.setItem(SESSION_KEY_FAILURE_HANDLING, val);
    // Dispatch a custom event so that FormSubmit.tsx updates its execution payload in sync
    window.dispatchEvent(new Event(CUSTOM_EVENT_STORAGE_UPDATE));
  };
  const [selectedTestPlan, setSelectedTestPlan] = useState<Option<TestPlanConfig> | null>(null);
  const [pendingTestPlan, setPendingTestPlan] = useState<Option<TestPlanConfig> | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [loadingTestCaseDetails, setLoadingTestCaseDetails] = useState(false);
  const [testPlanState, dispatchTestPlan] = useReducer(testPlanReducer, initialTestPlanState);
  const testCaseDetailsRequestRef = useRef(0);
  const [loadingTestPlan, setLoadingTestPlan] = useState(false);
  const planRequestIdRef = useRef(0);

  const normalizedTestCase = useMemo(() => {
    if (!selectedTestCase?.value) return null;
    return toTestCaseConfig(selectedTestCase.value);
  }, [selectedTestCase]);

  const normalizedPlanTestCases = useMemo(() => {
    if (!isTestPlan || !selectedTestPlan?.value?.testCases?.length) return [];
    const planId = selectedTestPlan.value.testPlanId;
    return selectedTestPlan.value.testCases
      .map((testCase, index) => {
        const option = toTestCaseOptionFromPlan(testCase, selectedTestPlan.value);
        // Plan identity forces a fresh form instance instead of reusing local state
        // when two plans contain the same test-case ID at the same position.
        return option
          ? { option, caseKey: `${planId}:${option.value.testCaseId}:${index}` }
          : null;
      })
      .filter((item): item is { option: Option<TestCaseConfig>; caseKey: string } => Boolean(item));
  }, [isTestPlan, selectedTestPlan]);

  // Reset all cross-mode/context state. TEST and TEST PLAN reuse this component
  // instance, so an explicit reset prevents a plan's first case from leaking
  // into standalone TEST mode (and vice versa).
  useEffect(() => {
    testCaseDetailsRequestRef.current += 1;
    planRequestIdRef.current += 1;
    setSelectedTestCase(null);
    setSelectedTestPlan(null);
    setPendingTestPlan(null);
    setLoadingTestCaseDetails(false);
    setLoadingTestPlan(false);
    setFormValues({});
    dispatchTestPlan({ type: "RESET" });
    clearStoredTestSelections();
    onFormChange({}, false);
    setFailureHandlingOption({ label: "Abort on Failure", value: "ABORT_ON_FAILURE" });
    sessionStorage.setItem(SESSION_KEY_FAILURE_HANDLING, "ABORT_ON_FAILURE");

    return () => {
      testCaseDetailsRequestRef.current += 1;
      planRequestIdRef.current += 1;
    };
  }, [mode, mission_id, sat_id, onFormChange]);

  // ── Sync selection state → sessionStorage ────────────────────────────────
  useEffect(() => {
    const selection = isTestPlan ? selectedTestPlan : selectedTestCase;
    if (selection) {
      // Full plan details can include large nested validation payloads. Keep
      // only lightweight selection metadata in browser storage.
      const persistedSelection = isTestPlan && selectedTestPlan
        ? {
            label: selectedTestPlan.label,
            value: {
              testPlanId: selectedTestPlan.value.testPlanId,
              testPlanName: selectedTestPlan.value.testPlanName,
            },
          }
        : selection;
      try {
        sessionStorage.setItem(keys.selection, JSON.stringify(persistedSelection));
      } catch {
        sessionStorage.removeItem(keys.selection);
      }
    } else {
      sessionStorage.removeItem(keys.selection);
    }
    if (selectedTestCase) {
      sessionStorage.setItem(keys.caseId, selectedTestCase.value.testCaseId);
    } else {
      sessionStorage.removeItem(keys.caseId);
    }
    if (isTestPlan) {
      if (selectedTestPlan) {
        sessionStorage.setItem("testPlanId", selectedTestPlan.value.testPlanId);
      } else {
        sessionStorage.removeItem("testPlanId");
      }
    }
    window.dispatchEvent(new Event(CUSTOM_EVENT_STORAGE_UPDATE));
  }, [isTestPlan, keys, selectedTestCase, selectedTestPlan]);

  // ── Sync form values → sessionStorage ────────────────────────────────────
  useEffect(() => {
    try {
      if (Object.keys(formValues).length > 0) {
        sessionStorage.setItem(keys.formValues, JSON.stringify(formValues));
      } else {
        sessionStorage.removeItem(keys.formValues);
      }
    } catch {
      // Storage quota must never break selection or execution state.
      sessionStorage.removeItem(keys.formValues);
    }
    window.dispatchEvent(new Event(CUSTOM_EVENT_STORAGE_UPDATE));
  }, [formValues, keys.formValues]);

  const fetchTestCaseDetails = useCallback(
    async (testCaseId: string, shell?: Partial<TestCaseConfig>): Promise<TestCaseConfig> => {
      if (!mission_id || !sat_id) {
        throw new Error("Missing mission or satellite id");
      }
      const res = await ValidateService.getTestCaseById(mission_id, testCaseId, sat_id);
      const detail = getResponseData(res);
      return {
        ...shell,
        ...detail,
        testCaseId,
      } as TestCaseConfig;
    },
    [mission_id, sat_id],
  );

  // ── Handlers — pure state updates only ───────────────────────────────────
  const handleTestCaseChange = useCallback(async (opt: Option<TestCaseConfig> | null) => {
    testCaseDetailsRequestRef.current += 1;
    const requestId = testCaseDetailsRequestRef.current;
    const shellOption = opt ? toTestCaseShellOption(opt.value, undefined, opt.label) : null;
    setSelectedTestCase(shellOption);
    setFormValues({});
    onFormChange({}, false);

    if (!shellOption) {
      setLoadingTestCaseDetails(false);
      return;
    }

    const testCaseId = shellOption.value.testCaseId;

    setLoadingTestCaseDetails(true);
    try {
      const detail = await fetchTestCaseDetails(testCaseId, shellOption.value);
      if (testCaseDetailsRequestRef.current !== requestId) return;
      setSelectedTestCase({
        ...shellOption,
        value: detail,
      });
    } catch {
      if (testCaseDetailsRequestRef.current === requestId) {
        setSelectedTestCase(null);
        showToastMessage("error", "Failed to load test case details");
      }
    } finally {
      if (testCaseDetailsRequestRef.current === requestId) {
        setLoadingTestCaseDetails(false);
      }
    }
  }, [fetchTestCaseDetails, onFormChange]);

  const handleTestPlanChange = useCallback(async (opt: Option<TestPlanConfig> | null) => {
    testCaseDetailsRequestRef.current += 1;
    const requestId = ++planRequestIdRef.current;
    setSelectedTestPlan(null);
    setSelectedTestCase(null);
    setPendingTestPlan(opt);
    setFormValues({});
    dispatchTestPlan({ type: "RESET" });
    clearStoredTestSelections();
    onFormChange({}, false);
    setFailureHandlingOption({ label: "Abort on Failure", value: "ABORT_ON_FAILURE" });
    sessionStorage.setItem(SESSION_KEY_FAILURE_HANDLING, "ABORT_ON_FAILURE");

    if (!opt) {
      setPendingTestPlan(null);
      setLoadingTestPlan(false);
      return;
    }
    if (!mission_id) {
      setPendingTestPlan(null);
      setLoadingTestPlan(false);
      showToastMessage("error", "Missing mission context");
      return;
    }

    const requestedPlanId = String(opt.value.testPlanId ?? "").trim();
    if (!requestedPlanId) {
      setPendingTestPlan(null);
      setLoadingTestPlan(false);
      showToastMessage("error", "Selected test plan has no valid ID");
      return;
    }

    setLoadingTestPlan(true);
    try {
      const res = await ValidateService.getTestPlanById(mission_id, requestedPlanId);
      if (requestId !== planRequestIdRef.current) return;

      const plan = extractTestPlanDetail(res);
      const planTestCases = Array.isArray(plan?.testCases) ? plan.testCases : [];
      if (!plan?.testPlanId) throw new Error("Test plan details are unavailable");
      if (String(plan.testPlanId).trim() !== requestedPlanId) {
        throw new Error("Received details for a different test plan");
      }
      if (plan.satelliteId && sat_id && String(plan.satelliteId) !== String(sat_id)) {
        throw new Error("Selected test plan does not belong to this satellite");
      }

      const normalizedCases = planTestCases.map((testCase) => toTestCaseOptionFromPlan(testCase, plan));
      if (normalizedCases.some((testCase) => testCase === null)) {
        throw new Error("Test plan contains incomplete test case details");
      }

      const hydratedOption: Option<TestPlanConfig> = { label: opt.label, value: { ...plan, testCases: planTestCases }, icon: opt.icon };
      setPendingTestPlan(null);
      setSelectedTestPlan(hydratedOption);

      if (planTestCases.length === 0) {
        setSelectedTestCase(null);
        showToastMessage("info", "No test cases are available under this test plan");
        return;
      }

      setSelectedTestCase(normalizedCases[0]);
    } catch (error: any) {
      if (requestId !== planRequestIdRef.current) return;
      setPendingTestPlan(null);
      setSelectedTestPlan(null);
      setSelectedTestCase(null);
      showToastMessage(
        "error",
        error?.response?.data?.message ?? error?.message ?? "Failed to load test plan details",
      );
    } finally {
      if (requestId === planRequestIdRef.current) setLoadingTestPlan(false);
    }
  }, [mission_id, sat_id, onFormChange]);

  const handleFormChange = useCallback(
    (values: Record<string, string>, isValid: boolean) => {
      setFormValues(values);
      onFormChange(values, isValid);
    },
    [onFormChange],
  );

  const handlePlanTestCaseFormChange = useCallback((caseKey: string, values: Record<string, string>, isValid: boolean) => {
    dispatchTestPlan({ type: "UPDATE_CASE", caseKey, values, isValid });
  }, []);

  // ── Derive merged form values from reducer state ──────────────────────────
  useEffect(() => {
    if (!isTestPlan) return;
    const mergedValues = Object.entries(testPlanState.valuesByCase).reduce<Record<string, string>>(
      (acc, [caseKey, values]) => {
        Object.entries(values).forEach(([key, value]) => {
          // Entry fallback keys commonly repeat across cases; namespace them so
          // stored overrides remain lossless for multi-case plans.
          acc[`${caseKey}.${key}`] = value;
        });
        return acc;
      },
      {},
    );
    const allValid =
      normalizedPlanTestCases.length > 0 &&
      normalizedPlanTestCases.every(({ caseKey }) => testPlanState.validityByCase[caseKey] === true);
    setFormValues(mergedValues);
    onFormChange(mergedValues, allValid);
  }, [isTestPlan, testPlanState, normalizedPlanTestCases, onFormChange]);

  return (
    <div
      className="left__panel__body__form__section__container modal__scrollbar__white"
      style={{ '--ts-max-h': `calc(100vh - ${255 + formContainerHeight}px)` } as CSSProperties}
    >
      <TestDropdown
        mode={mode}
        satelliteId={sat_id ?? ""}
        value={isTestPlan ? (selectedTestPlan ?? pendingTestPlan) : selectedTestCase}
        isResolvingSelection={isTestPlan && loadingTestPlan}
        onChange={isTestPlan
          ? (opt) => { void handleTestPlanChange(opt as Option<TestPlanConfig> | null); }
          : (opt) => handleTestCaseChange(opt as Option<TestCaseConfig> | null)
        }
      />

      {isTestPlan && loadingTestPlan && <TestPlanFormShimmer />}

      {isTestPlan && !loadingTestPlan && !!selectedTestPlan && selectedTestPlan.value?.isCritical && (
        <div
          className="left__panel__body__input__dropdown__container left__panel__body__input__custom__dropdown left__panel__body_input__border"
          data-testid="console-failure-handling-dropdown"
        >
          <span className="input__label">Failure Handling</span>
          <CustomSelect
            options={
              selectedTestPlan.value?.testCases?.length === 1
                ? [{ label: "Abort on Failure", value: "ABORT_ON_FAILURE" }]
                : [
                    { label: "Abort on Failure", value: "ABORT_ON_FAILURE" },
                    { label: "Skip & Continue", value: "SKIP_AND_CONTINUE" },
                  ]
            }
            value={failureHandlingOption}
            placeholder="Select option"
            onChange={(opt) => handleFailureHandlingChange(opt as Option<string> | null)}
            background="rgba(10,10,10,0.65)"
            isSearchable={false}
          />
        </div>
      )}

      {isTestPlan && !loadingTestPlan && selectedTestPlan?.value?.testCases?.length === 0 && (
        <div className="left__panel__body_input__border test__plan__tc__empty">
          No test cases in this plan
        </div>
      )}

      {loadingTestCaseDetails && !isTestPlan && <TestCaseDetailsShimmer />}

      {!loadingTestCaseDetails && normalizedTestCase && !isTestPlan && (
        <div className="test__plan__context">
          <span className="test__plan__tc__case__label" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {normalizedTestCase.isCritical && (
              <span className="test__plan__tc__case__critical-icon" style={{ display: 'inline-flex', alignItems: 'center' }}>
                <CriticalIcon />
              </span>
            )}
            <span>{selectedTestCase?.label}</span>
          </span>
          <TestForm testCase={normalizedTestCase} onFormChange={handleFormChange} />
        </div>
      )}

      {isTestPlan && normalizedPlanTestCases.length > 0 &&
        normalizedPlanTestCases.map(({ option: testCaseOpt, caseKey }) => (
          <div key={caseKey} className="test__plan__context">
            <span className="test__plan__tc__case__label" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              {testCaseOpt.value?.isCritical && (
                <span className="test__plan__tc__case__critical-icon" style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <CriticalIcon />
                </span>
              )}
              <span>{testCaseOpt.label}</span>
            </span>
            <TestForm
              testCase={testCaseOpt.value}
              onFormChange={(values, isValid) =>
                handlePlanTestCaseFormChange(caseKey, values, isValid)
              }
            />
          </div>
        ))
      }
    </div>
  );
};

export default TestFormSection;
